if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');
      }

      get variantIdSelect() {
        return this.querySelector('[name="id"]');
      }

      connectedCallback() {
        this.productFormActions = document.querySelector('.main-product-form');
        this.mainProductInfo = document.querySelector('product-info');
        this.container = this.closest('.sticky-atc-bar');
        
        // Add position class to body for padding adjustment
        const position = this.dataset.position || this.container?.dataset.position || 'bottom';
        const disableStickyHeader = this.dataset.disableStickyHeader === 'true';
        
        if (position === 'top') {
          document.body.classList.add('sticky-atc-bar-top');
          
          // Disable sticky header if option is enabled
          if (disableStickyHeader) {
            document.body.classList.add('sticky-atc-bar-disable-header');
            this.disableStickyHeader();
          }
        }

        this.variantData = this.getVariantData();
        this.select = this.querySelector('select');

        this.init();
        this.select.addEventListener('change', () => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.variantIdSelect.value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  element.value = variantOptionVal;
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          this.updatePrice();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        const position = this.dataset.position || this.container?.dataset.position || 'bottom';
        
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          if (position === 'top') {
            document.body.classList.add('sticky-atc-bar-visible');
          }
          return;
        }
        this.productId = this.dataset.productId;

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        
        // Initialize height - set to 0 for top position when hidden
        if (position === 'top') {
          document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px');
        } else {
          this.checkDevice();
        }

        // Track current state to prevent rapid toggling
        this.isBarVisible = false;
        this.debounceTimer = null;
        this.lastUpdateTime = 0;
        const DEBOUNCE_DELAY = 200; // Increased debounce delay for stability

        if (position === 'top') {
          // For top position, use scroll-based detection to avoid layout shift issues
          // Store the form's initial position relative to document (not affected by padding changes)
          this.updateFormPosition = () => {
            if (this.productFormActions) {
              const rect = this.productFormActions.getBoundingClientRect();
              this.formTopPosition = rect.top + window.scrollY;
              this.formHeight = rect.height;
            }
          };
          
          // Initialize form position
          this.updateFormPosition();
          
          // Update position on resize (but not on scroll, to avoid feedback)
          let resizeTimer;
          window.addEventListener('resize', () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
              this.updateFormPosition();
            }, 100);
          }, { passive: true });

          // Use scroll event with throttling for top position
          this.scrollTimer = null;
          this.scrollHandler = () => {
            if (this.scrollTimer) return; // Throttle
            
            this.scrollTimer = requestAnimationFrame(() => {
              this.scrollTimer = null;
              
              const now = Date.now();
              const currentScrollY = window.scrollY;
              
              // Calculate if form is scrolled past using initial position
              // This won't change even if padding is added/removed
              const formBottom = this.formTopPosition + this.formHeight;
              const showThreshold = formBottom - 100; // Show when form bottom is 100px above viewport top
              const hideThreshold = formBottom - 50; // Hide when form bottom is 50px above (hysteresis)
              
              let isScrolledPast;
              if (this.isBarVisible) {
                // Bar is visible - use hysteresis: require scrolling back up more to hide
                isScrolledPast = currentScrollY > hideThreshold;
              } else {
                // Bar is hidden - show when clearly scrolled past
                isScrolledPast = currentScrollY > showThreshold;
              }
              
              // Only update if state changed and enough time has passed
              if (this.isBarVisible !== isScrolledPast && (now - this.lastUpdateTime) >= DEBOUNCE_DELAY) {
                this.isBarVisible = isScrolledPast;
                this.lastUpdateTime = now;
                
                // Update visibility
                requestAnimationFrame(() => {
                  this.container.classList.toggle('sticky-atc-bar--show', isScrolledPast);
                  document.body.classList.toggle('sticky-atc-bar-visible', isScrolledPast);
                  
                  // Update height based on visibility
                  if (isScrolledPast) {
                    // Delay height measurement to ensure bar is fully visible
                    setTimeout(() => {
                      this.checkDevice();
                    }, 150);
                  } else {
                    document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px');
                  }
                });
              }
            });
          };
          
          // Use passive scroll listener for better performance
          window.addEventListener('scroll', this.scrollHandler, { passive: true });
          
        } else {
          // For bottom position, use IntersectionObserver (no layout shift issues)
          const rootMargin = `${this.productFormActions.offsetHeight}px 0px 0px 0px`;
          this.observer = new IntersectionObserver(
            (entries) => {
              const now = Date.now();
              
              // Clear any pending debounce
              if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
              }
              
              // Debounce updates to prevent rapid toggling
              this.debounceTimer = setTimeout(() => {
                entries.forEach((entry) => {
                  const formRect = entry.boundingClientRect;
                  const formTopFromViewport = formRect.top;
                  const buffer = 50;
                  const isScrolledPast = !entry.isIntersecting && formTopFromViewport < -buffer;
                  
                  // Only update if state actually changed and enough time has passed
                  if (this.isBarVisible !== isScrolledPast && (now - this.lastUpdateTime) >= DEBOUNCE_DELAY) {
                    this.isBarVisible = isScrolledPast;
                    this.lastUpdateTime = now;
                    
                    // Use requestAnimationFrame for smooth updates
                    requestAnimationFrame(() => {
                      this.container.classList.toggle('sticky-atc-bar--show', isScrolledPast);
                      
                      if (isScrolledPast) {
                        // For bottom position, update height when showing
                        this.checkDevice();
                      }
                    });
                  }
                });
                
                this.debounceTimer = null;
              }, DEBOUNCE_DELAY);
            },
            { 
              threshold: [0, 1], 
              rootMargin: rootMargin 
            }
          );
          this.observer.observe(this.productFormActions);
        }
        
        this.syncWithMainProductForm();
      }

      checkDevice() {
        const position = this.dataset.position || this.container?.dataset.position || 'bottom';
        const isVisible = this.container?.classList.contains('sticky-atc-bar--show');
        
        // For top position, only set height when visible to prevent white space
        if (position === 'top' && !isVisible) {
          document.documentElement.style.setProperty('--sticky-atc-bar-height', '0px');
        } else {
          document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
        }
      }

      disableStickyHeader() {
        // Remove header-pinned class to prevent sticky header
        document.body.classList.remove('header-pinned');
        
        // Prevent header from being pinned on scroll
        this.headerScrollHandler = () => {
          if (document.body.classList.contains('sticky-atc-bar-disable-header')) {
            document.body.classList.remove('header-pinned');
            const headerSection = document.querySelector('.header-section');
            if (headerSection) {
              headerSection.classList.remove('header-scrolled');
            }
          }
        };
        
        // Add scroll listener with higher priority
        window.addEventListener('scroll', this.headerScrollHandler, { passive: true, capture: true });
      }
      

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('.sticky-atc-bar__form');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = `<s>${FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat)}</s>`)
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${this._getBaseUnit()}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const isMainProduct = event.data.sectionId === this.mainProductInfo.dataset.section;
          if (!isMainProduct) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }
        });
      }

      disconnectedCallback() {
        // Clean up scroll handler for top position
        if (this.scrollHandler) {
          window.removeEventListener('scroll', this.scrollHandler);
        }
        
        // Cancel pending scroll animation frame
        if (this.scrollTimer) {
          cancelAnimationFrame(this.scrollTimer);
        }
        
        // Clean up observer for bottom position
        if (this.observer) {
          this.observer.disconnect();
        }
        
        // Clean up debounce timer
        if (this.debounceTimer) {
          clearTimeout(this.debounceTimer);
        }
        
        // Clean up header scroll handler
        if (this.headerScrollHandler) {
          window.removeEventListener('scroll', this.headerScrollHandler);
        }
      }
    }
  );
}
